import { dash } from 'radash'

import type { ComponentsTheme, SemanticToken, SemanticTokens, TenantTheme, TenantThemeColors } from '@/config/app/types'

import { toKebabCaseString } from '@/shared/lib'

// Хранилище CSS-переменных для накопления перед инъекцией
const cssVariablesBuffer = new Map<string, string>()

/**
 * Добавляет CSS-переменную в буфер
 * @param name - имя CSS-переменной
 * @param value - значение CSS-переменной
 */
function setCSSVariable(name: string, value: string): void {
  cssVariablesBuffer.set(name, value)
}

/**
 * Инъецирует накопленные CSS-переменные в отдельный тег <style> в <head>
 * Создает тег единожды или обновляет существующий
 */
function injectCSSVariables(): void {
  const STYLE_TAG_ID = 'tenant-theme-variables'

  // Находим или создаем тег <style>
  let styleTag = document.getElementById(STYLE_TAG_ID) as HTMLStyleElement | null

  if (!styleTag) {
    styleTag = document.createElement('style')
    styleTag.id = STYLE_TAG_ID
    document.head.appendChild(styleTag)
  }

  // Формируем CSS-правило с переменными
  const cssVariables = Array.from(cssVariablesBuffer.entries())
    .map(([name, value]) => `  ${name}: ${value};`)
    .join('\n')

  styleTag.textContent = `:root {\n${cssVariables}\n}`

  // Очищаем буфер после инъекции
  cssVariablesBuffer.clear()
}

/**
 * Рекурсивно устанавливает CSS переменные из объекта цветов
 * @param colors - объект с цветами
 * @param prefix - префикс для переменных
 */
function setCSSVariablesFromColors(colors: TenantThemeColors, prefix: string = 'color'): void {
  Object.entries(colors).forEach(([key, value]) => {
    const varName = toKebabCaseString(key)

    if (typeof value === 'string') {
      // Простое значение цвета
      setCSSVariable(`--${prefix}-${varName}`, value)
    } else if (typeof value === 'object' && value !== null) {
      // Вложенный объект цветов (например, Slate.50, Slate.100)
      Object.entries(value).forEach(([subKey, subValue]) => {
        const subVarName = toKebabCaseString(subKey)
        setCSSVariable(`--${prefix}-${varName}-${subVarName}`, subValue)
      })
    }
  })
}

/**
 * Устанавливает CSS переменные для fontSize
 * @param fontSize - объект с размерами шрифтов
 */
function setCSSVariablesFromFontSize(fontSize?: Record<string, string>): void {
  if (!fontSize) {
    return
  }

  Object.entries(fontSize).forEach(([key, value]) => {
    const varName = toKebabCaseString(key)
    setCSSVariable(`--font-size-${varName}`, value)
  })
}

/**
 * Устанавливает CSS переменные для fontFamily
 * @param fontFamily - объект со шрифтами
 */
function setCSSVariablesFromFontFamily(fontFamily?: Record<string, string>): void {
  if (!fontFamily) {
    return
  }

  Object.entries(fontFamily).forEach(([key, value]) => {
    const varName = toKebabCaseString(key)
    setCSSVariable(`--font-family-${varName}`, value)
  })
}

/**
 * Устанавливает CSS переменные для boxShadow
 * @param boxShadow - объект с тенями
 */
function setCSSVariablesFromBoxShadow(boxShadow?: Record<string, string>): void {
  if (!boxShadow) {
    return
  }

  Object.entries(boxShadow).forEach(([key, value]) => {
    const varName = toKebabCaseString(key)
    setCSSVariable(`--box-shadow-${varName}`, value)
  })
}

/**
 * Устанавливает CSS переменные для borderRadius
 * @param borderRadius - объект с радиусами границ
 */
function setCSSVariablesFromBorderRadius(borderRadius?: Record<string, string>): void {
  if (!borderRadius) {
    return
  }

  Object.entries(borderRadius).forEach(([key, value]) => {
    const varName = toKebabCaseString(key)
    setCSSVariable(`--border-radius-${varName}`, value)
  })
}

/**
 * Устанавливает CSS переменные для семантических токенов
 * @param semanticTokens - объект с семантическими токенами
 */
function setCSSVariablesFromSemanticTokens(semanticTokens?: SemanticTokens): void {
  if (!semanticTokens) {
    return
  }

  // Обрабатываем каждую категорию токенов (text, background, border, button, common, icon)
  Object.entries(semanticTokens).forEach(([category, tokens]) => {
    Object.entries(tokens as Record<string, SemanticToken>).forEach(([tokenName, tokenData]) => {
      const varName = toKebabCaseString(tokenName)
      const cssVarName = `--${category}-${varName}`

      setCSSVariable(cssVarName, tokenData.value)
    })
  })
}

/**
 * Устанавливает CSS переменные для конфигурации компонентов
 * @param config - объект с конфигурацией компонентов
 */
function setCSSVariablesFromComponents(config?: ComponentsTheme): void {
  if (!config) {
    return
  }

  const parseEntries = <T extends Record<string, string | number | T> | string | number>(entry: {
    key: string
    path: string[]
    value: T
  }) => {
    const currentPath = [...entry.path, entry.key]

    if (typeof entry.value === 'string' || typeof entry.value === 'number') {
      // Формируем полное имя переменной из всего пути
      const varName = dash(`--component-${currentPath.join('-')}`)
      const varValue = String(entry.value)

      setCSSVariable(varName, varValue)
    } else if (typeof entry.value === 'object' && entry.value !== null) {
      // Рекурсивно обходим вложенные объекты
      Object.entries(entry.value as Record<string, T>).forEach(([key, value]) => {
        parseEntries({
          key,
          path: currentPath,
          value,
        })
      })
    }
  }

  // Начинаем обход с пустого пути
  Object.entries(config).forEach(([key, value]) => {
    parseEntries({ key, path: [], value })
  })
}
/**
 * Применяет тему приложения, устанавливая CSS переменные
 * @param theme - конфигурация темы тенанта
 */
export function applyTheme(theme: TenantTheme): void {
  // Собираем все CSS-переменные в буфер
  setCSSVariablesFromColors(theme.colors)
  setCSSVariablesFromFontSize(theme.fontSize)
  setCSSVariablesFromFontFamily(theme.fontFamily)
  setCSSVariablesFromBoxShadow(theme.boxShadow)
  setCSSVariablesFromBorderRadius(theme.borderRadius)
  setCSSVariablesFromSemanticTokens(theme.semanticTokens)
  setCSSVariablesFromComponents(theme.components)

  // Инъецируем все накопленные переменные единожды
  injectCSSVariables()
}

/**
 * Composable для работы с темой приложения
 */
export function useTheme() {
  return {
    applyTheme,
  }
}
